/** Junior Eats — resolve fixed per-meal delivery times configured on the restaurant. */

const DEFAULT_TIMES_24 = {
    Breakfast: '09:00',
    Lunch: '13:00',
    Snacks: '16:00',
    Dinner: '20:00',
};

export function mealTypeFromOrder(order) {
    const meal =
        order?.mealType ??
        order?.meal_type ??
        order?.Meal_Type ??
        order?.meal ??
        order?.Meal;
    if (meal != null && String(meal).trim() !== '') return String(meal).trim();
    return '';
}

/** Map meal label to Breakfast | Lunch | Snacks | Dinner. */
export function normalizeMealSlotChip(raw) {
    const token = String(raw ?? '').trim().toLowerCase();
    if (!token) return null;
    if (token === 'breakfast') return 'Breakfast';
    if (token === 'lunch') return 'Lunch';
    if (token === 'dinner') return 'Dinner';
    if (token.includes('snack')) return 'Snacks';
    return null;
}

function normalizeTime24(raw) {
    if (raw == null) return null;
    const text = String(raw).trim();
    const match24 = /^(\d{1,2}):(\d{2})$/.exec(text);
    if (match24) {
        const hour = Number(match24[1]);
        const minute = Number(match24[2]);
        if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
            return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
        return null;
    }
    const match12 = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(text);
    if (match12) {
        let hour = Number(match12[1]);
        const minute = Number(match12[2]);
        const period = match12[3].toUpperCase();
        if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
        if (period === 'AM') hour = hour === 12 ? 0 : hour;
        else hour = hour === 12 ? 12 : hour + 12;
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
    return null;
}

export function formatTime12From24(time24) {
    const normalized = normalizeTime24(time24);
    if (!normalized) return null;
    const [hourStr, minuteStr] = normalized.split(':');
    const hourInt = Number(hourStr);
    const minuteInt = Number(minuteStr);
    const hour12 = hourInt % 12 || 12;
    const period = hourInt < 12 ? 'AM' : 'PM';
    return `${hour12}:${String(minuteInt).padStart(2, '0')} ${period}`;
}

export function getTime24ForMealSlot(timesPayload, mealSlotChip) {
    if (!mealSlotChip) return null;
    const entry = timesPayload?.[mealSlotChip];
    if (entry && typeof entry === 'object') {
        return (
            normalizeTime24(entry.time24) ??
            normalizeTime24(entry.time24h) ??
            normalizeTime24(entry.deliveryTime) ??
            normalizeTime24(entry.delivery_time)
        );
    }
    return normalizeTime24(DEFAULT_TIMES_24[mealSlotChip]);
}

function orderDateLocal(order) {
    const raw =
        order?.createdDate ??
        order?.createdAt ??
        order?.CreatedDate ??
        order?.created_date;
    if (raw) {
        const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(String(raw))
            ? String(raw).replace(' ', 'T')
            : raw;
        const parsed = new Date(normalized);
        if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
}

/** API datetime "YYYY-MM-DD HH:mm:ss" from order date + restaurant meal delivery time. */
export function buildJreatsApiDeliveryTime(order, timesPayload) {
    const mealSlot = normalizeMealSlotChip(mealTypeFromOrder(order));
    const time24 =
        getTime24ForMealSlot(timesPayload, mealSlot) ??
        normalizeTime24(DEFAULT_TIMES_24.Lunch);
    if (!time24) return null;
    const [hour, minute] = time24.split(':').map(Number);
    const d = orderDateLocal(order);
    d.setHours(hour, minute, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

export function formatJreatsMealDeliveryDisplay(order, timesPayload) {
    const meal = mealTypeFromOrder(order);
    const mealSlot = normalizeMealSlotChip(meal);
    const time24 = getTime24ForMealSlot(timesPayload, mealSlot);
    const label = time24 ? formatTime12From24(time24) : null;
    if (meal && label) return `${meal} · ${label}`;
    if (label) return label;
    return '—';
}
